import environment from '../../../common/environment';
import store from '../../../store/store';
import { ErrorCode } from '../../../common/utils/error-code-utils';
import { InstructorSampleAnalytics } from '../instructor-sample.analytics';
import { MessageType } from '../../alert/global-message.interface';
import {
  PROFESSIONAL_CR_EDUCATORS_URL,
  PROFESSIONAL_EDUCATORS_BOARDS_AND_BEYONDS_URL,
  PROFESSIONAL_FA4_EDUCATORS_URL,
} from '../../../common/constants/professional-redirects';
import { SHARPEN_LEARN_MORE_URL } from '../../upsell/constants/sharpen-page-type.constants';
import { StoreState } from '../../../store/store.interface';
import { html, nothing, TemplateResult } from 'lit';
import { redirectToHigherEd, redirectToProductOrHome } from './instructor-sample.util';

/**
 * Gets the error message when an instructor tries to add a forbidden product for sampling.
 *
 * @param errorCode
 * @param element
 */
export function getCartSamplingErrorMessage(
  errorCode: ErrorCode,
  element?: HTMLElement
): TemplateResult | typeof nothing {
  switch (errorCode) {
    case ErrorCode.HE_INSTRUCTOR_CART_EMPTY:
      return renderErrorAlert("You haven't added any samples to your cart.", MessageType.info, 'info-circle', element);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_BOARDS_BEYOND:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-boards-and-beyonds-products-not-allowed', element);
      return renderProfessionalProductErrorMessage(PROFESSIONAL_EDUCATORS_BOARDS_AND_BEYONDS_URL);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_CLINICAL_REASONING:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-clinical-reasoning-products-not-allowed', element);
      return renderProfessionalProductErrorMessage(PROFESSIONAL_CR_EDUCATORS_URL);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_FIRST_AID_FORWARD:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-first-aid-forward-products-not-allowed', element);
      return renderProfessionalProductErrorMessage(PROFESSIONAL_FA4_EDUCATORS_URL);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_SHARPEN:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-sharpen-product-not-allowed', element);
      return renderProfessionalProductErrorMessage(SHARPEN_LEARN_MORE_URL);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_K12:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-k12-products-not-allowed', element);
      return renderDisplayErrorMessage("We're sorry, K12 products cannot be sampled.", element);
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_MHP:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-mhp-products-not-allowed', element);
      return renderDisplayErrorMessage(
        `We're sorry, this is a student-only product and cannot be sampled.`,
        element,
        true
      );
    case ErrorCode.HE_INSTRUCTOR_GUEST_PRODUCTS_REMOVED:
      InstructorSampleAnalytics.emit('mhecomm-instructor-sampling-not-allowed', element);
      return renderErrorAlert(
        "One or more products in your cart are not available for sampling and have been removed.",
        MessageType.danger,
        'exclamation-triangle',
        element
      );
    default:
      return nothing;
  }
}

function renderErrorAlert(
  message: string,
  type: MessageType,
  icon: string,
  element?: HTMLElement,
  isMhpProduct?: boolean
): TemplateResult {
  return html`
    <style>
      #errorAlert {
        margin-bottom: 500px;
      }
    </style>
    <div
      id="errorAlert"
      class="alert alert-${type} alert-dismissible d-flex align-items-top"
      role="alert"
      aria-label="${message} Continue browsing for samples.">
      <uxt-icon icon="${icon}" class="me-3"></uxt-icon>
      <div>
        ${message}
        <button
          type="button"
          class="btn btn-link p-0 align-baseline"
          @click=${() =>
            isMhpProduct ? redirectToHigherEd() : redirectToProductOrHome(store.getState() as StoreState, element)}>
          Continue browsing for samples.
        </button>
      </div>
    </div>
  `;
}

function renderDisplayErrorMessage(message: string, element?: HTMLElement, isMhpProduct?: boolean): TemplateResult {
  return html`
    ${message}
    <a
      @click=${() => InstructorSampleAnalytics.emit('continue-browsing-to-sample', element)}
      href="${isMhpProduct ? `${environment().mheducationUrl}/highered` : environment().mheducationUrl}"
      target="_blank">
      Continue browsing for samples<span class="visually-hidden">(opens in a new tab)</span></a
    >.
  `;
}

function renderProfessionalProductErrorMessage(learnMoreLink: string): TemplateResult {
  return html`
    <div>
      We're sorry, this is a student-only product and cannot be sampled.
      ${learnMoreLink
        ? html`&nbsp;If you're interested in this product for your institution,
            <a id="learn-more-link" href="${learnMoreLink}" target="_blank">
              learn more here<span class="visually-hidden">(opens in a new tab)</span></a
            >.`
        : nothing}
    </div>
  `;
}
